/**
 * Learning Goals API Client with React Query Hooks
 *
 * Provides mutation hooks for learning goal management.
 * Learning goals are loaded from initial data, so no fetch hooks needed.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPut } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Update a learning goal's localized name for a specific language
 * Story 15.8: Learning goal IDs are now integers
 */
const updateLearningGoalLocalization = async (
  learningGoalId: number,
  languageCode: string,
  localizedName: string
): Promise<{ learning_goal_id: number; language_code: string; localized_name: string }> => {
  return apiPut(`/api/admin/learning-goals/${learningGoalId}/localizations/${languageCode}`, {
    localized_name: localizedName,
  });
};

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Update a learning goal's localized name
 */
export const useUpdateLearningGoalLocalization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      learningGoalId,
      languageCode,
      localizedName,
    }: {
      learningGoalId: number;
      languageCode: string;
      localizedName: string;
    }) => updateLearningGoalLocalization(learningGoalId, languageCode, localizedName),
    onSuccess: () => {
      // Invalidate initial data to reload learning goals with updated localization
      queryClient.invalidateQueries({ queryKey: ['initialData'] });
    },
    onError: (error) => {
      logger.error('Error updating learning goal localization:', error);
    },
  });
};
